'use client'

import type { ReactNode } from 'react'

import { getJourneyAccessState } from '@/features/journeys/domain/getJourneyAccessState'
import { useJourneysStore } from '@/features/journeys'
import { useAppRouter } from '@/shared/lib/router'
import { Page } from '@/shared/ui/Page'
import { UnavailableState } from '@/shared/ui/UnavailableState'

interface LevelAccessGuardProps {
  levelUid: string
  children: ReactNode
}

/**
 * Barra a entrada num nível cuja jornada está bloqueada por `unlock_journey_id`.
 *
 * O bloqueio era aplicado só no card da lista de Jornadas, então qualquer outra
 * entrada — Campanhas → Desafios → Jornada, ou o link direto `/niveis/{uid}` —
 * levava o aluno para dentro do nível e ele só era barrado ao responder, com um
 * 422 `journey_locked` vindo da API.
 *
 * Fica FORA do LevelProvider de propósito: assim a jornada bloqueada nem chega
 * a ser buscada (a API agora responde 403 nessa leitura) e o aluno vê o motivo
 * em vez de um erro de carregamento.
 *
 * Só bloqueia quando o veredito é positivo: enquanto a store carrega, ou quando
 * o nível não é encontrado nela, a renderização segue e quem decide é a API —
 * um falso bloqueio aqui trancaria um aluno legítimo fora do conteúdo.
 */
export function LevelAccessGuard({ levelUid, children }: LevelAccessGuardProps) {
  const router = useAppRouter()
  const { getLevelByUid, getJourneyProgress } = useJourneysStore()

  const found = getLevelByUid(levelUid)
  const journeyProgress = found ? getJourneyProgress(found.journey) : null

  const accessState = getJourneyAccessState({
    currentJourney: found?.journey ?? null,
    lockState: journeyProgress
      ? {
          blocked: journeyProgress.blocked,
          blockedByJourneyName: journeyProgress.blockedByJourneyName
        }
      : undefined
  })

  if (accessState.kind === 'journey_locked') {
    return (
      <Page className="bg-[#378bea] px-0 lg:px-0">
        <UnavailableState
          title={accessState.title}
          description={accessState.description}
          onAction={() => router.push('/jornadas')}
        />
      </Page>
    )
  }

  return <>{children}</>
}
